import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { posApi, authApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { useSyncStore } from '@/store/syncStore';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import SyncModeBanner from '@/components/pos/SyncModeBanner';
import { ShoppingCart, Users, Receipt, ArrowRight, Menu, CheckCircle } from 'lucide-react';
import PakRupeeIcon from '@/components/common/PakRupeeIcon';
import { formatMoney } from '@/utils/currency';
import { Link } from 'react-router-dom';
import { isCompanyAdmin } from '@/types';
import { isLocalFirstMode, localDailySummary } from '@/offline';
import PendingShiftApprovals from '@/components/dashboard/PendingShiftApprovals';

function translateSubscriptionLabel(value: string | undefined, t: (key: string, opts?: { defaultValue?: string }) => string): string {
  if (!value) return '';
  const key = value.toLowerCase().replace(/\s+/g, '_');
  return t(`subscription.${key}`, { defaultValue: value });
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user, company, role } = useAuthStore();
  const { toggleSidebar } = useUIStore();
  const posSyncMode = useSyncStore((s) => s.posSyncMode);
  const { data: summary } = useQuery({
    queryKey: ['dailySummary', posSyncMode],
    queryFn: async () => {
      if (isLocalFirstMode()) {
        return localDailySummary();
      }
      const res = await posApi.getDailySummary();
      return res.data.message;
    },
  });
  const { data: profile } = useQuery({
    queryKey: ['profile'], queryFn: async () => {
      const res = await authApi.getCompanyProfile(); return res.data.message;
    }
  });

  const stats = [
    { key: 'totalSales', label: t('dashboard.totalSales'), value: formatMoney(summary?.total_sales || 0), icon: PakRupeeIcon, color: 'bg-emerald-500' },
    { key: 'invoices', label: t('dashboard.invoices'), value: summary?.invoice_count || 0, icon: Receipt, color: 'bg-blue-500' },
    { key: 'productSales', label: t('dashboard.productSales'), value: formatMoney(summary?.item_sales || 0), icon: ShoppingCart, color: 'bg-amber-500' },
    { key: 'serviceSales', label: t('dashboard.serviceSales'), value: formatMoney(summary?.service_sales || 0), icon: Users, color: 'bg-purple-500' },
  ];

  const quickActions = [
    { key: 'newSale', label: t('dashboard.newSale'), desc: t('dashboard.newSaleDesc'), to: '/', color: 'bg-primary-600' },
    { key: 'products', label: t('nav.products'), desc: t('dashboard.productsDesc'), to: '/items', color: 'bg-slate-700' },
    { key: 'customers', label: t('nav.customers'), desc: t('dashboard.customersDesc'), to: '/customers', color: 'bg-slate-700' },
    { key: 'analytics', label: t('nav.analytics'), desc: t('dashboard.analyticsDesc'), to: '/analytics', color: 'bg-slate-700' },
    { key: 'reports', label: t('nav.reports'), desc: t('dashboard.reportsDesc'), to: '/reports/sales/register', color: 'bg-slate-700' },
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <SyncModeBanner />
        <TrialBanner />
        {isLocalFirstMode() && (
          <p className="mx-6 mt-2 text-xs text-slate-500">{t('dashboard.localDataNote')}</p>
        )}
        <div className="p-6">
          <div className="mb-6 flex items-start sm:items-center justify-between gap-3">
            <div className="flex items-start sm:items-center gap-3">
              <button onClick={toggleSidebar} className="lg:hidden p-2 -ml-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg shrink-0">
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
                <p className="text-gray-500 text-sm">{t('common.welcomeBack', { name: user?.full_name, company: company?.name })}</p>
              </div>
            </div>

            {isCompanyAdmin(role) && profile?.status === 'Active' && !profile?.is_nearing_expiration && (
              <Link to="/support" className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-full transition-colors border border-emerald-100">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">{t('common.subscribed')}</span>
              </Link>
            )}
          </div>

          {(isCompanyAdmin(role) || role === 'Manager') && (
            <PendingShiftApprovals />
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {stats.map((s) => (
              <div key={s.key} className="card p-5">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 ${s.color} rounded-lg flex items-center justify-center`}>
                    <s.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{s.label}</p>
                    <p className="text-xl font-bold text-gray-900">{s.value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 xs:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {quickActions.map((a) => (
              <Link key={a.key} to={a.to} className={`${a.color} rounded-xl p-5 text-white hover:opacity-90 transition-opacity`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{a.label}</p>
                    <p className="text-sm opacity-80">{a.desc}</p>
                  </div>
                  <ArrowRight className="w-5 h-5" />
                </div>
              </Link>
            ))}
          </div>

          {/* Company Info */}
          {profile && (
            <div className="card p-5">
              <h3 className="font-semibold text-gray-900 mb-3">{t('dashboard.companyInfo')}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><p className="text-gray-500">{t('common.status')}</p><p className="font-medium">{translateSubscriptionLabel(profile.status, t)}</p></div>
                <div><p className="text-gray-500">{t('common.package')}</p><p className="font-medium">{translateSubscriptionLabel(profile.package?.name, t) || t('common.none')}</p></div>
                {profile.status !== 'Active' && (
                  <div><p className="text-gray-500">{t('dashboard.trialEnds')}</p><p className="font-medium">{profile.trial_end || t('common.na')}</p></div>
                )}
                {profile.subscription_details && (
                  <>
                    <div><p className="text-gray-500">{t('dashboard.subStart')}</p><p className="font-medium">{profile.subscription_details.start_date}</p></div>
                    <div><p className="text-gray-500">{t('dashboard.subEnd')}</p><p className="font-medium">{profile.subscription_details.end_date}</p></div>
                  </>
                )}
                <div><p className="text-gray-500">{t('common.owner')}</p><p className="font-medium">{profile.owner_name}</p></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
